import { useCallback, useEffect, useMemo, useState } from "react";
import { Chip, Tooltip, Typography } from "@mui/material";
import { WarningAmberOutlined as WarningIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { fetchImportJobs } from "@accredo/fe-shared/services/importJobService.js";
import ImportJobDetailModal from "../ImportJobDetailModal.jsx";

function statusChipColor(status) {
  if (["completed", "rolled_back"].includes(status)) return "success";
  if (["processing", "rollback_processing"].includes(status)) return "warning";
  if (["failed", "cancelled", "rollback_failed"].includes(status))
    return "error";
  return "default";
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ImportJobsTable() {
  const { enqueueSnackbar } = useSnackbar();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchImportJobs()
      .then((res) => {
        setJobs(res.data ?? []);
        setHasLoaded(true);
      })
      .catch(() =>
        enqueueSnackbar("Jobs konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const has_running = jobs.some((j) =>
    ["processing", "rollback_processing"].includes(j.status)
  );

  useEffect(() => {
    const id = setInterval(load, has_running ? 5000 : 15000);
    return () => clearInterval(id);
  }, [has_running, load]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "#",
        size: 70,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        id: "connector_name",
        header: "Connector",
        accessorFn: (row) => row.connector?.name ?? "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.status_label}
            color={statusChipColor(row.original.status)}
          />
        ),
      },
      {
        accessorKey: "tasks_count",
        header: "Tasks",
        size: 100,
        muiTableHeadCellProps: { align: "right" },
        Cell: ({ row }) => {
          const total = row.original.tasks_count ?? 0;
          const with_errors = row.original.tasks_with_errors_count ?? 0;
          const ok = total - with_errors;
          return (
            <Typography
              variant="body2"
              sx={{
                textAlign: "right",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.5,
              }}
            >
              {with_errors > 0 && (
                <Tooltip title={`${with_errors} Task(s) mit Fehlern`}>
                  <WarningIcon
                    sx={{ fontSize: "1rem", color: "warning.main" }}
                  />
                </Tooltip>
              )}
              {ok}/{total}
            </Typography>
          );
        },
      },
      {
        id: "created_by_name",
        header: "Erstellt von",
        accessorFn: (row) => row.created_by?.name ?? "System",
      },
      {
        accessorKey: "started_at",
        header: "Gestartet",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "finished_at",
        header: "Abgeschlossen",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "created_at",
        header: "Erstellt am",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
    ],
    []
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={jobs}
        isLoading={loading}
        hasLoaded={hasLoaded}
        manualSorting={false}
        manualFiltering={false}
        enableGlobalFilter={true}
        enableColumnFilters={false}
        onRowClick={(row) => setSelectedId(row.id)}
      />

      <ImportJobDetailModal
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        jobId={selectedId}
      />
    </>
  );
}
